import { goToLogin } from "@/lib/api";
import {
  I18nProvider,
  Layout,
  SidebarAccount,
  type SidebarItem,
} from "@omnitok/ui";
import {
  BookOpen,
  CircleHelp,
  Database,
  FolderOpen,
  Mail,
  Palette,
  PlugZap,
  Settings2,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import {
  ScreenHelpPanel,
  topicForLocation,
  type HelpTopic,
} from "@/components/help/ScreenHelp";
import { useAuth } from "@/hooks/useAuth";
import { useT, type Locale } from "@/i18n";
import { adminIssuesApi } from "@/lib/admin-ai-api";
import { slugFromPath } from "@/lib/tenant-path";
import versionInfo from "../../../../version.json";

/**
 * Sentinel id for the contextual-help entry in the AYUDA group. It is
 * deliberately NOT a path: `onSidebarItemClick` navigates to `item.id`
 * for every other entry, so anything path-shaped here would route.
 */
const HELP_ITEM_ID = "__screen_help__";

/** Ids of the sidebar section captions; like the help entry, never paths. */
const SECTION_IDS = {
  intelligence: "__section_intelligence__",
  admin: "__section_admin__",
  help: "__section_help__",
} as const;

// The builder is an Omnitok tool, so the brand header carries the Omnitok
// mark and no "powered by" (D27: the portal's v3 shell with another label).
// Hoisted once so React doesn't re-create the <img> on every render; the
// ``fetchPriority="high"`` hint pairs with a preload link in ``index.html``.
const BRAND_LOGO = (
  <img src="/omnitok-logo.svg" alt="Omnitok" fetchPriority="high" />
);

const LANGUAGES = [{ code: "en" }, { code: "es" }, { code: "pt" }];

/**
 * Authenticated shell.
 *
 * Wraps every page (except `/login`) in the kit's v3 ``<Layout>``, the
 * same shell as the portal and the console (D27):
 *   - brand header: Omnitok mark + "Builder · {tenant}", so an operator
 *     always sees which customer they are editing;
 *   - framed navy sidebar, its groups as labelled sections
 *     (Inteligencia, Administración, Ayuda), a 64 px rail when collapsed;
 *   - ``SidebarAccount`` at the foot: version, languages as
 *     buttons, sign out and who is signed in.
 * The page header sits inside ``<Outlet />``.
 */
export function AppShell(): JSX.Element {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, locale, setLocale } = useT();

  // D2 (PR-5): area flags from /me — admins come back all-true from
  // the backend, so the flags alone drive visibility.
  const canManageUsers = Boolean(user?.builder_access?.manage_users);
  const canManageAi = Boolean(user?.builder_access?.manage_ai);
  const canOperate = Boolean(user?.builder_access?.operations);
  const isAdmin = user?.role === "admin";

  // ── Contextual help rail (Plan J J2, D.2) ──────────────────────
  // The console passes `onHelp` into every page because its App.tsx
  // renders each one by hand; the builder renders through `<Outlet />`
  // and cannot. So the SHELL owns both halves: it resolves the current
  // screen's topic from the location and puts the trigger in the AYUDA
  // group, beside the Guía, where somebody looking for help already
  // looks. The entry only exists on a screen that HAS help — its
  // appearing is itself the signal.
  const helpTopic: HelpTopic | null = useMemo(
    () => topicForLocation(location.pathname, location.search),
    [location.pathname, location.search],
  );
  const [helpOpen, setHelpOpen] = useState(false);
  // A rail about the previous screen sitting over a different one is a
  // rail about the wrong thing — the console clears it on navigation
  // for the same reason (its App.tsx:188). `helpTopic` changes with
  // the query string too, so switching Datos › Fuente ↔ Dimensiones
  // closes it as well.
  useEffect(() => setHelpOpen(false), [helpTopic]);

  // PR-4: open-issues count for the "Panorama IA" sidebar badge.
  // `null` = not loaded / fetch failed → badge simply omitted (the
  // Panorama page itself surfaces the real error on open).
  const [openIssueCount, setOpenIssueCount] = useState<number | null>(null);
  useEffect(() => {
    if (!canManageAi) {
      setOpenIssueCount(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const metrics = await adminIssuesApi.metrics();
        if (!cancelled) setOpenIssueCount(metrics.open_count);
      } catch {
        if (!cancelled) setOpenIssueCount(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canManageAi, location.pathname]);

  const sidebarItems: SidebarItem[] = useMemo(() => {
    // Projects + Reports live in one page at `/` (iter 27, PR-0 reorg);
    // the components gallery is for every authenticated user, so editors
    // learning the system have a discoverable catalog (D28: it lives
    // inside this shell).
    const items: SidebarItem[] = [
      {
        id: "/",
        label: t("nav.projects"),
        icon: <FolderOpen size={16} />,
      },
      {
        id: "/components",
        label: t("nav.components"),
        icon: <Palette size={16} />,
      },
    ];
    // INTELIGENCIA: "Panorama IA" (PR-4) holds the global prompts as a
    // tab (/admin/prompts redirects there). Gated by `manage_ai` (PR-5).
    if (canManageAi) {
      items.push({
        id: SECTION_IDS.intelligence,
        kind: "section",
        label: t("nav.section.intelligence"),
        children: [
          {
            id: "/admin/ai",
            label: t("nav.admin.panorama"),
            icon: <Sparkles size={16} />,
            badge:
              openIssueCount !== null && openIssueCount > 0
                ? openIssueCount
                : undefined,
          },
        ],
      });
    }
    // ADMINISTRACIÓN: Accesos (Plan J J3, flag `manage_users`; it
    // replaced "Usuarios y permisos" and lands on Ámbitos, the tab the
    // other three depend on), Operaciones + Integraciones (flag
    // `operations`; Plan C 2.1+2.3: one manager for channels and data
    // sources) and the user-send policy (Plan F F4, role admin).
    const admin: SidebarItem[] = [];
    if (canManageUsers) {
      admin.push({
        id: "/admin/access/scopes",
        label: t("nav.admin.access"),
        icon: <ShieldCheck size={16} />,
      });
    }
    if (canOperate) {
      admin.push(
        {
          id: "/admin/operations",
          label: t("nav.admin.operations"),
          icon: <Settings2 size={16} />,
        },
        // What every reload computes before it publishes (2026-10-26).
        {
          id: "/admin/cache",
          label: t("nav.admin.cache"),
          icon: <Database size={16} />,
        },
        {
          id: "/admin/integrations",
          label: t("nav.admin.integrations"),
          icon: <PlugZap size={16} />,
        },
      );
    }
    if (isAdmin) {
      admin.push({
        id: "/admin/send-policy",
        label: t("nav.admin.sendPolicy"),
        icon: <Mail size={16} />,
      });
    }
    if (admin.length > 0) {
      items.push({
        id: SECTION_IDS.admin,
        kind: "section",
        label: t("nav.section.admin"),
        children: admin,
      });
    }
    // AYUDA: contextual help for THIS screen (only where there is any),
    // then the static guide. Visible to everyone who can enter.
    const help: SidebarItem[] = [];
    if (helpTopic !== null) {
      help.push({
        id: HELP_ITEM_ID,
        label: t("screenhelp.button"),
        icon: <CircleHelp size={16} />,
      });
    }
    help.push({
      id: "/guide",
      label: t("nav.guide"),
      icon: <BookOpen size={16} />,
    });
    items.push({
      id: SECTION_IDS.help,
      kind: "section",
      label: t("nav.section.help"),
      children: help,
    });
    return items;
  }, [
    t,
    canManageUsers,
    canManageAi,
    canOperate,
    isAdmin,
    openIssueCount,
    helpTopic,
  ]);

  const activePath = useMemo(() => {
    if (location.pathname.startsWith("/templates")) return "/templates";
    // /projects redirects to `/` (iter 27 — unified list). Highlight
    // the unified "Reports & Projects" item when on either path.
    if (location.pathname.startsWith("/projects")) return "/";
    if (location.pathname.startsWith("/components")) return "/components";
    // All four Accesos sections and their detail pages light the one
    // sidebar entry. /admin/users is here too: it redirects, so the
    // pathname is only ever this for the instant before the redirect
    // commits — but an entry that flickers to "nothing selected" on the
    // way is worse than one that never does.
    if (
      location.pathname.startsWith("/admin/access") ||
      location.pathname.startsWith("/admin/users")
    ) {
      return "/admin/access/scopes";
    }
    if (location.pathname.startsWith("/admin/operations")) {
      return "/admin/operations";
    }
    if (location.pathname.startsWith("/admin/cache")) {
      return "/admin/cache";
    }
    if (location.pathname.startsWith("/admin/integrations")) {
      return "/admin/integrations";
    }
    if (location.pathname.startsWith("/admin/send-policy")) {
      return "/admin/send-policy";
    }
    if (location.pathname.startsWith("/guide")) return "/guide";
    // Prompts (list redirect + /:id editor) belong to Panorama IA now.
    if (
      location.pathname.startsWith("/admin/ai") ||
      location.pathname.startsWith("/admin/prompts")
    ) {
      return "/admin/ai";
    }
    return "/";
  }, [location.pathname]);

  const tenant = slugFromPath();
  const signOut = () => {
    if (window.confirm(t("auth.logoutConfirm"))) {
      logout();
      goToLogin();
    }
  };

  return (
    // The kit's own strings (menu toggle, drawer close) follow the
    // builder's language.
    <I18nProvider language={locale}>
      <Layout
        brandHeader={{
          brand: BRAND_LOGO,
          // The slug is what the operator typed and what every API call
          // carries. Null cannot reach an authenticated shell (main.tsx
          // sends it to the login page); if it did, the label says only
          // "Builder" rather than naming a customer.
          label:
            tenant === null
              ? t("shell.label")
              : t("shell.labelTenant", { tenant }),
          poweredBy: false,
          menuLabel: t("shell.toggleMenu"),
        }}
        sidebarItems={sidebarItems}
        activeSidebarId={activePath}
        onSidebarItemClick={(item: SidebarItem) => {
          // The help entry opens the rail; section captions are not
          // destinations (navigating to their id would bounce to `/`).
          if (item.id === HELP_ITEM_ID) setHelpOpen(true);
          else if (!item.id.startsWith("__")) navigate(item.id);
        }}
        rightPanel={
          helpOpen && helpTopic !== null ? (
            <ScreenHelpPanel
              topic={helpTopic}
              onClose={() => setHelpOpen(false)}
              onOpenGuide={() => {
                setHelpOpen(false);
                navigate("/guide");
              }}
            />
          ) : undefined
        }
        // The <aside> is rendered only when `rightPanel` is truthy, so
        // this width costs nothing while the rail is closed. Below `md`
        // the kit gives the panel the full width and hides <main> behind
        // it: on a phone the help REPLACES the screen it is about, and
        // the X is the way back (same in the console).
        rightPanelWidth="26rem"
        sidebarFooter={
          user ? (
            <SidebarAccount
              name={user.name}
              detail={user.email ?? user.role ?? ""}
              // Bumped on every CI run via .github/scripts/bump_version.py:
              // confirms at a glance which build is live.
              version={versionInfo.version}
              languages={LANGUAGES}
              language={locale}
              onLanguageChange={(code) => setLocale(code as Locale)}
              languageLabel={t("header.lang")}
              onSignOut={signOut}
              signOutLabel={t("header.signOut")}
            />
          ) : undefined
        }
        sidebarFooterCollapsed={
          user ? <SidebarAccount collapsed name={user.name} /> : undefined
        }
      >
        <Outlet />
      </Layout>
    </I18nProvider>
  );
}
